import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

const API = 'https://cna.clublostortugas.com/api/api.php';

const Dashboard = () => {
    const [inscripciones, setInscripciones] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState('inscripciones');
    const [eventos, setEventos] = useState([]);
    const [selectedEventoId, setSelectedEventoId] = useState(null);
    const [activeEventoId, setActiveEventoId] = useState(null);
    const itemsPerPage = 20;
    const navigate = useNavigate();

    useEffect(() => {
        fetchEventos();
        fetchAdmins();
    }, []);

    useEffect(() => {
        if (selectedEventoId !== null) {
            fetchData(selectedEventoId);
        }
    }, [selectedEventoId]);

    const fetchEventos = async () => {
        try {
            const res = await api.get(`${API}?action=get_events`);
            if (res.data.success) {
                setEventos(res.data.data);
                const activo = res.data.data.find(e => e.activo == 1);
                if (activo) {
                    setActiveEventoId(activo.id);
                    setSelectedEventoId(activo.id);
                } else if (res.data.data.length > 0) {
                    setSelectedEventoId(res.data.data[0].id);
                }
            }
        } catch (error) {
            console.error('Error fetching eventos', error);
        }
    };

    const fetchData = async (eventoId) => {
        setLoading(true);
        try {
            const params = eventoId ? `${API}?action=get_all_forms&evento_id=${eventoId}` : `${API}?action=get_all_forms`;
            const res = await api.get(params);
            if (res.data.success) {
                setInscripciones(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching data', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAdmins = async () => {
        try {
            const res = await api.get(`${API}?action=get_admins`);
            if (res.data.success) {
                setAdmins(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching admins', error);
        }
    };

    const handleSetActiveEvent = async (id) => {
        try {
            const res = await api.post(`${API}?action=set_active_event`, { id });
            if (res.data.success) {
                setActiveEventoId(id);
                setEventos(prev => prev.map(e => ({ ...e, activo: e.id === id ? 1 : 0 })));
                Swal.fire({
                    icon: 'success', title: '¡Evento Activado!',
                    text: 'El formulario público ahora recibe inscripciones para este evento.',
                    confirmButtonColor: '#008C44',
                    customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                    buttonsStyling: false
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error', title: 'Error',
                text: error.response?.data?.error || 'No se pudo activar el evento.',
                confirmButtonColor: '#d33',
                customClass: { confirmButton: 'btn btn-danger rounded-pill px-4' },
                buttonsStyling: false
            });
        }
    };

    const handleCreateEvent = () => {
        Swal.fire({
            title: '<i class="bi bi-plus-circle me-2"></i>Crear Nuevo Evento CNA',
            html: `
                <div class="text-start">
                    <label class="form-label small fw-bold">Nombre del Evento / Válida <span class="text-danger">*</span></label>
                    <input type="text" id="swal-ev-valida" class="form-control rounded-pill mb-3" placeholder="Ej. CNA 9 MAYO 2026">
                    <label class="form-label small fw-bold">Ciudad</label>
                    <input type="text" id="swal-ev-ciudad" class="form-control rounded-pill mb-3" placeholder="Ej. Bogotá">
                    <div class="alert alert-info mt-2 small rounded-3 mb-0">
                        <i class="bi bi-info-circle me-1"></i> Al crear el evento, quedará <strong>activo automáticamente</strong> y el formulario público comenzará a recibir inscripciones para este evento.
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: '<i class="bi bi-check-circle me-1"></i> Crear y Activar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#008C44',
            customClass: {
                confirmButton: 'btn btn-club rounded-pill px-4 shadow-sm me-2',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-4 shadow-sm'
            },
            buttonsStyling: false,
            preConfirm: () => {
                const valida = document.getElementById('swal-ev-valida').value.trim();
                const ciudad = document.getElementById('swal-ev-ciudad').value.trim();
                if (!valida) {
                    Swal.showValidationMessage('El nombre del evento es obligatorio');
                    return false;
                }
                return { valida, ciudad };
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.post(`${API}?action=create_event`, result.value);
                    if (res.data.success) {
                        Swal.fire({
                            icon: 'success', title: '¡Evento Creado!',
                            text: 'El nuevo evento está activo. El formulario público ya recibe inscripciones.',
                            confirmButtonColor: '#008C44',
                            customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                            buttonsStyling: false
                        });
                        await fetchEventos();
                    }
                } catch (error) {
                    Swal.fire({
                        icon: 'error', title: 'Error',
                        text: error.response?.data?.error || 'Error al crear el evento.',
                        confirmButtonColor: '#d33',
                        customClass: { confirmButton: 'btn btn-danger rounded-pill px-4' },
                        buttonsStyling: false
                    });
                }
            }
        });
    };

    const handleDeleteEvent = async () => {
        if (!selectedEventoId) return;
        const ev = eventos.find(e => e.id === selectedEventoId);
        if (ev && ev.activo == 1) {
            Swal.fire({
                icon: 'warning', title: 'No permitido',
                text: 'No puedes eliminar el evento activo. Activa otro evento primero.',
                confirmButtonColor: '#008C44',
                customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                buttonsStyling: false
            });
            return;
        }
        const confirm = await Swal.fire({
            icon: 'warning',
            title: '¿Eliminar evento?',
            html: `¿Seguro que deseas eliminar <strong>${ev?.valida}</strong>?<br><small class="text-muted">Esta acción no se puede deshacer.</small>`,
            showCancelButton: true,
            confirmButtonText: '<i class="bi bi-trash me-1"></i> Sí, eliminar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
            customClass: {
                confirmButton: 'btn btn-danger rounded-pill px-4 me-2',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-4'
            },
            buttonsStyling: false
        });
        if (confirm.isConfirmed) {
            try {
                const res = await api.post(`${API}?action=delete_event`, { id: selectedEventoId });
                if (res.data.success) {
                    Swal.fire({
                        icon: 'success', title: 'Eliminado',
                        text: 'El evento fue eliminado correctamente.',
                        confirmButtonColor: '#008C44',
                        customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                        buttonsStyling: false
                    });
                    await fetchEventos();
                }
            } catch (error) {
                Swal.fire({
                    icon: 'error', title: 'Error',
                    text: error.response?.data?.error || 'No se pudo eliminar el evento.',
                    confirmButtonColor: '#d33',
                    customClass: { confirmButton: 'btn btn-danger rounded-pill px-4' },
                    buttonsStyling: false
                });
            }
        }
    };

    const handleCloseLanding = async () => {
        const confirm = await Swal.fire({
            icon: 'warning',
            title: '¿Cerrar inscripciones?',
            html: 'Se activará la <strong>landing informativa</strong> y el formulario público dejará de estar disponible.<br><small class="text-muted">Puedes reabrir las inscripciones activando el evento desde el selector.</small>',
            showCancelButton: true,
            confirmButtonText: '<i class="bi bi-door-closed me-1"></i> Cerrar inscripciones',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#6c757d',
            customClass: {
                confirmButton: 'btn btn-secondary rounded-pill px-4 me-2',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-4'
            },
            buttonsStyling: false
        });
        if (confirm.isConfirmed) {
            try {
                const res = await api.post(`${API}?action=close_registrations`);
                if (res.data.success) {
                    Swal.fire({
                        icon: 'success', title: 'Landing activada',
                        text: 'El formulario público está cerrado. Los usuarios verán la página informativa.',
                        confirmButtonColor: '#008C44',
                        customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                        buttonsStyling: false
                    });
                    setActiveEventoId(null);
                    await fetchEventos();
                }
            } catch (error) {
                Swal.fire({
                    icon: 'error', title: 'Error',
                    text: error.response?.data?.error || 'No se pudo cerrar las inscripciones.',
                    confirmButtonColor: '#d33',
                    customClass: { confirmButton: 'btn btn-danger rounded-pill px-4' },
                    buttonsStyling: false
                });
            }
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin');
    };

    const handleExportExcel = () => {
        const eventoNombre = eventos.find(e => e.id === selectedEventoId)?.valida || 'evento';
        const rows = inscripciones.map(item => {
            const pilotos = Array.isArray(item.datos_pilotos) ? item.datos_pilotos : [];
            const baseRow = {
                'ID': item.id,
                'Válida': item.valida,
                'Fecha Inscripción': item.fecha,
                'Ciudad': item.ciudad,
                'No. Vehículo': item.no_vehiculo,
                'Nombre Equipo': item.nombre_equipo,
                'Categoría': item.categoria,
            };
            pilotos.forEach((p, i) => {
                const n = i + 1;
                baseRow[`Piloto ${n} - Nombre`] = p.nombres || '';
                baseRow[`Piloto ${n} - CC`] = p.cc || '';
                baseRow[`Piloto ${n} - Lic. FCAD`] = p.lic_fcad || '';
                baseRow[`Piloto ${n} - Club`] = p.club || '';
                baseRow[`Piloto ${n} - Celular`] = p.celular || '';
                baseRow[`Piloto ${n} - EPS`] = p.eps || '';
                baseRow[`Piloto ${n} - No. Afiliación`] = p.no_afiliacion || '';
                baseRow[`Piloto ${n} - RH`] = p.rh || '';
                baseRow[`Piloto ${n} - Email`] = p.email || '';
                baseRow[`Piloto ${n} - Ciudad`] = p.ciudad || '';
            });
            return baseRow;
        });

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Inscripciones');
        const fileName = `CNA_${eventoNombre.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const verPDF = (id) => {
        window.open(`/pdf/${id}`, '_blank');
    };

    const editarFormulario = (id) => {
        navigate(`/editar/${id}`);
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: "Esta acción no se puede deshacer.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#008C44',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            customClass: {
                confirmButton: 'btn btn-club rounded-pill px-4 shadow-sm me-2',
                cancelButton: 'btn btn-outline-danger rounded-pill px-4 shadow-sm'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.post(`${API}?action=delete_form`, { id });
                    if (res.data.success) {
                        setInscripciones(inscripciones.filter(item => item.id !== id));
                        if (currentItems.length === 1 && currentPage > 1) {
                            setCurrentPage(currentPage - 1);
                        }
                        Swal.fire({
                            title: 'Eliminado!',
                            text: 'La inscripción ha sido eliminada.',
                            icon: 'success',
                            confirmButtonColor: '#008C44',
                            customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                            buttonsStyling: false
                        });
                    }
                } catch (error) {
                    console.error("Error deleting data", error);
                    Swal.fire({
                        title: 'Error',
                        text: error.response?.data?.error || 'Error al eliminar la inscripción',
                        icon: 'error',
                        confirmButtonColor: '#d33',
                        customClass: { confirmButton: 'btn btn-danger rounded-pill px-4' },
                        buttonsStyling: false
                    });
                }
            }
        });
    };

    const handleDeleteAdmin = async (id) => {
        if (id === 1) {
            Swal.fire({
                title: 'Acceso Denegado',
                text: 'No se puede eliminar el administrador principal.',
                icon: 'error',
                confirmButtonColor: '#008C44',
                customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                buttonsStyling: false
            });
            return;
        }

        Swal.fire({
            title: '¿Eliminar Administrador?',
            text: "¿Estás seguro de que deseas eliminar este usuario? Perderá acceso al sistema.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6e7881',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
            customClass: {
                confirmButton: 'btn btn-danger rounded-pill px-4 shadow-sm me-2',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-4 shadow-sm'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.post(`${API}?action=delete_admin`, { id });
                    if (res.data.success) {
                        setAdmins(admins.filter(a => a.id !== id));
                        Swal.fire({
                            title: 'Eliminado',
                            text: 'El usuario administrador ha sido eliminado.',
                            icon: 'success',
                            confirmButtonColor: '#008C44',
                            customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                            buttonsStyling: false
                        });
                    }
                } catch (error) {
                    Swal.fire({
                        title: 'Error',
                        text: error.response?.data?.error || 'No se pudo eliminar al usuario.',
                        icon: 'error',
                        confirmButtonColor: '#008C44',
                        customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                        buttonsStyling: false
                    });
                }
            }
        });
    };

    const handleCreateAdmin = () => {
        Swal.fire({
            title: 'Crear Nuevo Administrador',
            html: `
                <div class="text-start">
                    <label class="form-label small fw-bold">Nombre de Usuario</label>
                    <input type="text" id="swal-username" class="form-control rounded-pill mb-3" placeholder="Ej. admin2">
                    <label class="form-label small fw-bold">Contraseña</label>
                    <input type="password" id="swal-password" class="form-control rounded-pill" placeholder="Contraseña segura">
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: 'Crear Usuario',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#008C44',
            customClass: {
                confirmButton: 'btn btn-club rounded-pill px-4 shadow-sm me-2',
                cancelButton: 'btn btn-outline-secondary rounded-pill px-4 shadow-sm'
            },
            buttonsStyling: false,
            preConfirm: () => {
                const username = document.getElementById('swal-username').value;
                const password = document.getElementById('swal-password').value;
                if (!username || !password) {
                    Swal.showValidationMessage('Por favor completa todos los campos');
                }
                return { username, password };
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await api.post(`${API}?action=create_admin`, result.value);
                    if (res.data.success) {
                        Swal.fire({
                            icon: 'success',
                            title: '¡Creado!',
                            text: 'El nuevo administrador ha sido registrado.',
                            confirmButtonColor: '#008C44',
                            customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                            buttonsStyling: false
                        });
                        fetchAdmins();
                    }
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: error.response?.data?.error || 'Error al crear el usuario.',
                        confirmButtonColor: '#008C44',
                        customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                        buttonsStyling: false
                    });
                }
            }
        });
    };

    // Filtering logic (Inscripciones)
    const filteredInscripciones = inscripciones.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        return (
            (item.valida && item.valida.toLowerCase().includes(searchLower)) ||
            (item.nombre_equipo && item.nombre_equipo.toLowerCase().includes(searchLower)) ||
            (item.categoria && item.categoria.toLowerCase().includes(searchLower)) ||
            (item.no_vehiculo && item.no_vehiculo.toLowerCase().includes(searchLower))
        );
    });

    // Pagination logic (Inscripciones)
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredInscripciones.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredInscripciones.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    return (
        <div className="dashboard-wrapper min-vh-100 bg-light pb-5">
            <nav className="navbar navbar-dark bg-dark shadow-sm py-3 border-bottom border-club border-4">
                <div className="container">
                    <div className="d-flex align-items-center">
                        <img
                            src="/img/logo-tortugas.png"
                            alt="Logo"
                            style={{ maxHeight: '50px' }}
                            className="me-3"
                        />
                        <span className="navbar-brand fw-bold mb-0 h4">Panel de Administración</span>
                    </div>
                    <button className="btn btn-outline-light rounded-pill px-4 btn-sm" onClick={handleLogout}>
                        <i className="bi bi-box-arrow-right me-2"></i>Cerrar Sesión
                    </button>
                </div>
            </nav>

            <div className="container mt-5">
                {/* Tabs Navigation */}
                <div className="d-flex mb-4 gap-2">
                    <button
                        className={`btn rounded-pill px-4 fw-bold shadow-sm ${activeTab === 'inscripciones' ? 'btn-club' : 'btn-white'}`}
                        onClick={() => setActiveTab('inscripciones')}
                    >
                        <i className="bi bi-list-check me-2"></i>Inscripciones
                    </button>
                    <button
                        className={`btn rounded-pill px-4 fw-bold shadow-sm ${activeTab === 'usuarios' ? 'btn-club' : 'btn-white'}`}
                        onClick={() => setActiveTab('usuarios')}
                    >
                        <i className="bi bi-people me-2"></i>Usuarios / Admins
                    </button>
                </div>

                <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-body p-4">
                        {activeTab === 'inscripciones' ? (
                            <>
                                {/* Selector de Evento */}
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-4 p-3 bg-light rounded-4 border">
                                    <i className="bi bi-calendar3 text-club fs-5"></i>
                                    <span className="fw-bold text-dark small">EVENTO:</span>
                                    <select
                                        className="form-select form-select-sm rounded-pill border-0 shadow-sm"
                                        style={{ maxWidth: '300px' }}
                                        value={selectedEventoId || ''}
                                        onChange={(e) => {
                                            setSelectedEventoId(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        {eventos.map(ev => (
                                            <option key={ev.id} value={ev.id}>
                                                {ev.valida} — {ev.fecha}{ev.activo == 1 ? ' ✓ ACTIVO' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedEventoId && selectedEventoId !== activeEventoId && (
                                        <button
                                            className="btn btn-sm btn-outline-success rounded-pill px-3"
                                            onClick={() => handleSetActiveEvent(selectedEventoId)}
                                        >
                                            <i className="bi bi-lightning-charge me-1"></i>Activar este evento
                                        </button>
                                    )}
                                    {selectedEventoId === activeEventoId && activeEventoId !== null && (
                                        <>
                                            <span className="badge bg-success rounded-pill px-3 py-2">
                                                <i className="bi bi-check-circle me-1"></i>Formulario público activo
                                            </span>
                                            <button
                                                className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                                                onClick={handleCloseLanding}
                                                title="Activar landing informativa y cerrar el formulario público"
                                            >
                                                <i className="bi bi-door-closed me-1"></i>Cerrar inscripciones
                                            </button>
                                        </>
                                    )}
                                    <button
                                        className="btn btn-sm btn-club rounded-pill px-3 ms-auto shadow-sm"
                                        onClick={handleCreateEvent}
                                    >
                                        <i className="bi bi-plus-circle me-1"></i>Nuevo Evento CNA
                                    </button>
                                    {selectedEventoId && selectedEventoId !== activeEventoId && (
                                        <button
                                            className="btn btn-sm btn-outline-danger rounded-pill px-3 shadow-sm"
                                            onClick={handleDeleteEvent}
                                        >
                                            <i className="bi bi-trash me-1"></i>Eliminar evento
                                        </button>
                                    )}
                                </div>

                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                                    <h2 className="fw-bold text-dark mb-0">
                                        {eventos.find(e => e.id === selectedEventoId)?.valida || 'Inscripciones'}
                                    </h2>
                                    <div className="d-flex flex-column flex-md-row gap-2" style={{ flex: 1, maxWidth: '500px' }}>
                                        <input
                                            type="text"
                                            className="form-control rounded-pill border-0 bg-light"
                                            placeholder="Buscar por Equipo, Categoría o Vehículo..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setCurrentPage(1);
                                            }}
                                        />
                                        <button className="btn btn-club rounded-pill px-4 shadow-sm text-nowrap" onClick={() => fetchData(selectedEventoId)}>
                                            <i className="bi bi-arrow-clockwise me-2"></i>Actualizar
                                        </button>
                                        <button
                                            className="btn btn-success rounded-pill px-4 shadow-sm text-nowrap"
                                            onClick={handleExportExcel}
                                            disabled={inscripciones.length === 0}
                                        >
                                            <i className="bi bi-file-earmark-excel me-2"></i>Exportar Excel
                                        </button>
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-club" role="status"></div>
                                        <p className="mt-3 text-muted">Cargando registros...</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="table-responsive rounded-3 border">
                                            <table className="table table-hover align-middle mb-0">
                                                <thead className="bg-light">
                                                    <tr>
                                                        <th className="px-4 py-3 fw-bold text-muted small">VÁLIDA / EVENTO</th>
                                                        <th className="py-3 fw-bold text-muted small">EQUIPO</th>
                                                        <th className="py-3 fw-bold text-muted small text-center">CATEGORÍA</th>
                                                        <th className="py-3 fw-bold text-muted small text-center">No. VEHÍCULO</th>
                                                        <th className="py-3 fw-bold text-muted small text-center">ACCIONES</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentItems.length === 0 ? (
                                                        <tr><td colSpan="5" className="text-center py-5 text-muted">No hay inscripciones registradas.</td></tr>
                                                    ) : (
                                                        currentItems.map((item) => (
                                                            <tr key={item.id}>
                                                                <td className="px-4">
                                                                    <div className="fw-bold text-club">{item.valida}</div>
                                                                    <div className="small text-muted">{item.fecha}</div>
                                                                </td>
                                                                <td className="fw-bold text-dark">
                                                                    {item.nombre_equipo || 'Sin equipo'}
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="badge bg-light text-dark border">{item.categoria}</span>
                                                                </td>
                                                                <td className="text-center fw-bold fs-5 text-club">
                                                                    {item.no_vehiculo || '-'}
                                                                </td>
                                                                <td className="text-center">
                                                                    <div className="btn-group">
                                                                        <button
                                                                            className="btn btn-sm btn-club rounded-pill px-3 shadow-sm me-2"
                                                                            onClick={() => verPDF(item.id)}
                                                                        >
                                                                            <i className="bi bi-file-earmark-pdf me-1"></i>PDF
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-warning rounded-pill px-3 shadow-sm me-2"
                                                                            onClick={() => editarFormulario(item.id)}
                                                                        >
                                                                            <i className="bi bi-pencil-square me-1"></i>Editar
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-dark rounded-pill px-3 shadow-sm me-2"
                                                                            onClick={() => {
                                                                                let pilotosConArl = '';
                                                                                let countArl = 0;

                                                                                if (item.datos_pilotos && Array.isArray(item.datos_pilotos)) {
                                                                                    item.datos_pilotos.forEach((piloto, index) => {
                                                                                        if (piloto.arl_url) {
                                                                                            countArl++;
                                                                                            pilotosConArl += `
                                                                                                <div class="mb-3 text-start bg-light p-3 rounded-3 border">
                                                                                                    <div class="fw-bold mb-1"><i class="bi bi-person-badge me-2 text-club"></i>Piloto ${index + 1}: ${piloto.nombres || 'Sin nombre'}</div>
                                                                                                    <a href="https://cna.clublostortugas.com/api/${piloto.arl_url}" target="_blank" rel="noopener noreferrer" class="btn btn-sm btn-primary rounded-pill px-3 shadow-sm mt-2">
                                                                                                        <i class="bi bi-box-arrow-up-right me-2"></i>Ver Certificado ARL
                                                                                                    </a>
                                                                                                </div>
                                                                                            `;
                                                                                        }
                                                                                    });
                                                                                }

                                                                                if (countArl === 0) {
                                                                                    Swal.fire({
                                                                                        title: 'Sin ARL',
                                                                                        text: 'Ninguno de los pilotos adjuntó el certificado de ARL en esta inscripción.',
                                                                                        icon: 'info',
                                                                                        confirmButtonColor: '#008C44',
                                                                                        customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                                                                                        buttonsStyling: false
                                                                                    });
                                                                                } else {
                                                                                    Swal.fire({
                                                                                        title: 'Certificados ARL',
                                                                                        html: pilotosConArl,
                                                                                        icon: 'info',
                                                                                        confirmButtonColor: '#008C44',
                                                                                        confirmButtonText: 'Cerrar',
                                                                                        customClass: { confirmButton: 'btn btn-club rounded-pill px-4' },
                                                                                        buttonsStyling: false
                                                                                    });
                                                                                }
                                                                            }}
                                                                        >
                                                                            <i className="bi bi-shield-check me-1"></i>ARL
                                                                        </button>
                                                                        <button
                                                                            className="btn btn-sm btn-outline-danger rounded-pill px-3 shadow-sm"
                                                                            onClick={() => handleDelete(item.id)}
                                                                        >
                                                                            <i className="bi bi-trash"></i>
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                        {totalPages > 1 && (
                                            <div className="d-flex justify-content-center mt-4 mb-2">
                                                <nav aria-label="Navegación de páginas">
                                                    <ul className="pagination pagination-sm mb-0 shadow-sm rounded-pill overflow-hidden">
                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                            <button className="page-link border-0 bg-light text-dark px-3" onClick={() => handlePageChange(currentPage - 1)}>Anterior</button>
                                                        </li>
                                                        {[...Array(totalPages)].map((_, i) => (
                                                            <li key={i + 1} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                                                                <button
                                                                    className={`page-link border-0 px-3 ${currentPage === i + 1 ? 'text-white' : 'bg-light text-dark'}`}
                                                                    style={currentPage === i + 1 ? { backgroundColor: '#008C44' } : {}}
                                                                    onClick={() => handlePageChange(i + 1)}
                                                                >
                                                                    {i + 1}
                                                                </button>
                                                            </li>
                                                        ))}
                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                            <button className="page-link border-0 bg-light text-dark px-3" onClick={() => handlePageChange(currentPage + 1)}>Siguiente</button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h2 className="fw-bold text-dark mb-0">Gestión de Usuarios</h2>
                                    <button className="btn btn-club rounded-pill px-4 shadow-sm" onClick={handleCreateAdmin}>
                                        <i className="bi bi-person-plus me-2"></i>Crear Nuevo Admin
                                    </button>
                                </div>

                                <div className="table-responsive rounded-3 border">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="px-4 py-3 fw-bold text-muted small">USUARIO</th>
                                                <th className="py-3 fw-bold text-muted small">FECHA CREACIÓN</th>
                                                <th className="py-3 fw-bold text-muted small text-center">ACCIONES</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {admins.map((admin) => (
                                                <tr key={admin.id}>
                                                    <td className="px-4 fw-bold">{admin.username}</td>
                                                    <td>{admin.created_at}</td>
                                                    <td className="text-center">
                                                        {admin.id !== 1 && (
                                                            <button
                                                                className="btn btn-sm btn-outline-danger rounded-pill px-3"
                                                                onClick={() => handleDeleteAdmin(admin.id)}
                                                            >
                                                                <i className="bi bi-trash me-2"></i>Eliminar
                                                            </button>
                                                        )}
                                                        {admin.id === 1 && <span className="badge bg-secondary rounded-pill px-3">Principal</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <p className="text-center mt-4 text-muted small">© 2026 Club Los Tortugas - Sistema de Gestión Técnica</p>
            </div>
        </div>
    );
};

export default Dashboard;
